'use client';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { AgentLineDialogue } from '@/app/components/icons/AgentLineDialogue';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { SITE } from '@/app/constants';
import { reportEvent } from '@/app/utils';
import { ChannelBindModal } from './ChannelBindModal';
import { ChannelConfigModal } from './ChannelConfigModal';
import { CHANNEL_CONFIGS } from './constants';
import type { ChannelItem } from './types';

interface ChannelConfigButtonProps {
  clawId: string;
  variant?: 'card' | 'nav';
  onBindSuccess?: () => void;
}

type TimerRef = ReturnType<typeof setTimeout>;

export const ChannelConfigButton = ({ clawId, variant = 'card', onBindSuccess }: ChannelConfigButtonProps) => {
  const t = useTranslations('claw.channelConfig');
  const [activeChannel, setActiveChannel] = useState<ChannelItem | null>(null);
  const [isNavMenuOpen, setIsNavMenuOpen] = useState(false);
  const closeTimerRef = useRef<TimerRef | null>(null);

  const configs = CHANNEL_CONFIGS[SITE === 'china' ? 'china' : 'global'];

  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openNavMenu = () => {
    clearCloseTimer();
    setIsNavMenuOpen(true);
  };

  const scheduleCloseNavMenu = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setIsNavMenuOpen(false);
    }, 80);
  };

  const handleSelectChannel = (channel: ChannelItem) => {
    if (channel.elementName) {
      reportEvent('claw_element_click', { element_name: channel.elementName });
    }
    clearCloseTimer();
    setActiveChannel(channel);
    setIsNavMenuOpen(false);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const renderNavButton = () => (
    <DropdownMenu open={isNavMenuOpen} onOpenChange={setIsNavMenuOpen}>
      <div onMouseEnter={openNavMenu} onMouseLeave={scheduleCloseNavMenu}>
        <DropdownMenuTrigger asChild>
          <div
            className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-12 bg-system-white04 light:bg-[rgba(130,132,172,0.16)] px-4 py-2 light:text-black text-system-white hover:bg-system-white08 light:hover:bg-[rgba(130,132,172,0.24)]"
            onPointerDown={(event) => event.preventDefault()}
          >
            {/* 默认 dark 用基础对话气泡；light 切换为带星星款，靠 variant 切换避免在组件里读 useTheme。 */}
            <AgentLineDialogue className="light:hidden size-6 shrink-0" />
            <AgentLineDialogue withStar className="light:block hidden size-6 shrink-0 text-black" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-[14px] leading-[1.2]">{t('navTrigger.title')}</p>
              <p className="light:text-[rgba(0,0,0,0.4)] text-[12px] text-system-white48 leading-[1.35]">
                {t('navTrigger.description')}
              </p>
            </div>
            <BaseLineRight className="size-4 shrink-0" />
          </div>
        </DropdownMenuTrigger>
      </div>
      <DropdownMenuContent
        side="right"
        align="center"
        sideOffset={8}
        className="w-[180px] min-w-[180px] rounded-8 border border-system-white12 bg-[#1A1A1A]/95 light:bg-system-bg02 p-2 light:shadow-[0px_20px_60px_0px_rgba(0,0,0,0.18)] shadow-[0px_20px_60px_0px_rgba(0,0,0,0.32)] backdrop-blur-[12px]"
        onMouseEnter={openNavMenu}
        onMouseLeave={scheduleCloseNavMenu}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        {configs.map((config) => {
          const Icon = config.icon;
          return (
            <DropdownMenuItem
              key={config.id}
              className="min-h-12 gap-2 rounded-8 px-2 py-3 text-sm text-system-text01 data-[highlighted]:bg-system-white10 data-[highlighted]:text-system-text01"
              onSelect={(event) => {
                event.preventDefault();
                handleSelectChannel(config);
              }}
            >
              <Icon className="size-6 shrink-0" />
              <span className="flex-1 text-left">{t(config.titleKey.menu)}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderCardButton = () => {
    const config = configs[0];
    if (!config) return null;

    const Icon = config.icon;

    return (
      <button
        type="button"
        className="relative flex h-[78px] w-full cursor-pointer items-start justify-center gap-4 overflow-hidden rounded-16 bg-system-white04 light:bg-[rgba(130,132,172,0.16)] px-5 py-4 text-left transition-colors hover:bg-system-white08 light:hover:bg-[rgba(130,132,172,0.24)]"
        aria-label={t(config.titleKey.card)}
        onClick={() => handleSelectChannel(config)}
      >
        <div className="flex min-w-0 flex-1 flex-col items-start gap-1 whitespace-nowrap leading-[1.4]">
          <p className="font-semibold light:text-black text-base text-system-white">{t(config.titleKey.card)}</p>
          <p className="light:text-[rgba(0,0,0,0.4)] text-sm text-system-white36">{t(config.descriptionKey.card)}</p>
        </div>
        <div className="pointer-events-none relative h-10 min-w-0 flex-1 py-2">
          <div className="absolute top-2 left-[67px] h-[109px] w-[158px] rounded-12 border border-system-white16 light:border-[#ECECEC] bg-system-black36 light:bg-white">
            <div className="absolute top-2 left-[42px] h-[109px] w-[108px] rounded-r-8 bg-system-white08 light:bg-[rgba(117,117,117,0.08)]" />
          </div>
          <Icon className="absolute top-[-1px] left-[204px] size-10" />
        </div>
        <div className="-translate-x-1/2 pointer-events-none absolute bottom-0 left-1/2 light:hidden h-[27px] w-[310px] bg-[linear-gradient(0deg,#26292B_0%,rgba(38,41,43,0)_100%)]" />
      </button>
    );
  };

  return (
    <>
      {variant === 'nav' ? renderNavButton() : renderCardButton()}

      {activeChannel?.type === 'qrcode' && (
        <ChannelBindModal
          channel={activeChannel}
          open
          onOpenChange={(open) => {
            if (!open) {
              setActiveChannel(null);
            }
          }}
          clawId={clawId}
          onSuccess={onBindSuccess}
        />
      )}

      {activeChannel?.type === 'config' && (
        <ChannelConfigModal
          channel={activeChannel}
          open
          onOpenChange={(open) => {
            if (!open) {
              setActiveChannel(null);
            }
          }}
          clawId={clawId}
          onSuccess={onBindSuccess}
        />
      )}
    </>
  );
};
